/**
 * hhm-testimonial-slider: Swiper init + GSAP animations
 * - Scroll-triggered: section header (badge, title) stagger reveal
 * - Slider track fade-up on scroll
 * - Slide change: active slide content animates in (logo, quote, desc, author)
 * - Card hover: subtle scale and shadow
 */
(function () {
	'use strict';

	const easeSnappy = 'power2.out';
	const easeBack = 'back.out(1.1)';

	function animateSlideContent(card) {
		if (!card || typeof gsap === 'undefined') return;
		const logo = card.querySelector('.hhm-testimonial-slider__logo');
		const quote = card.querySelector('.hhm-testimonial-slider__quote');
		const desc = card.querySelector('.hhm-testimonial-slider__desc');
		const author = card.querySelector('.hhm-testimonial-slider__author');
		const content = card.querySelector('.hhm-testimonial-slider__content');
		const els = [logo, quote, desc, author].filter(Boolean);
		if (!els.length) return;
		gsap.set(els, { opacity: 0, y: 16 });
		gsap.to(els, {
			opacity: 1,
			y: 0,
			duration: 0.45,
			stagger: 0.06,
			ease: easeSnappy,
		});
	}

	function initSection(section) {
		const track = section.querySelector('.hhm-testimonial-slider__track');
		const swiperEl = section.querySelector('.swiper');
		const header = section.querySelector('.hhm-testimonial-slider-header');
		const hasSwiper = typeof Swiper !== 'undefined' && swiperEl;

		// ----- 1. GSAP: scroll-triggered header + track -----
		if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
			gsap.registerPlugin(ScrollTrigger);

			if (header) {
				const badge = header.querySelector('.hhm-testimonial-slider-header__badge');
				const title = header.querySelector('.hhm-testimonial-slider-header__title');
				const headerEls = [badge, title].filter(Boolean);
				if (headerEls.length) {
					gsap.set(headerEls, { opacity: 0, y: 28 });
					gsap.to(headerEls, {
						opacity: 1,
						y: 0,
						duration: 0.65,
						stagger: 0.1,
						ease: easeSnappy,
						scrollTrigger: {
							trigger: header,
							start: 'top 88%',
							toggleActions: 'play none none none',
						},
					});
				}
			}

			if (track) {
				gsap.set(track, { opacity: 0, y: 32 });
				gsap.to(track, {
					opacity: 1,
					y: 0,
					duration: 0.7,
					ease: easeBack,
					scrollTrigger: {
						trigger: track,
						start: 'top 88%',
						toggleActions: 'play none none none',
					},
				});
			}
		}

		// ----- 2. Swiper init -----
		if (hasSwiper) {
			const swiper = new Swiper(swiperEl, {
				direction: 'horizontal',
				loop: true,
				slidesPerView: 1,
				spaceBetween: 20,
				centeredSlides: false,
				pagination: {
					el: section.querySelector('.swiper-pagination'),
					clickable: true,
					dynamicBullets: true,
				},
				navigation: {
					nextEl: section.querySelector('.swiper-button-next'),
					prevEl: section.querySelector('.swiper-button-prev'),
				},
				autoplay: {
					delay: 5000,
					disableOnInteraction: false,
					pauseOnMouseEnter: true,
				},
				breakpoints: {
					640: { slidesPerView: 1, spaceBetween: 20 },
					768: { slidesPerView: 2, spaceBetween: 24 },
				},
				a11y: {
					prevSlideMessage: 'Previous testimonial',
					nextSlideMessage: 'Next testimonial',
					paginationBulletMessage: 'Go to testimonial {{index}}',
				},
				on: {
					init: function () {
						// Animate visible slide(s) on load
						const slides = this.slides;
						const activeSlide = slides[this.activeIndex];
						const card = activeSlide && activeSlide.querySelector('.hhm-testimonial-slider__card');
						if (card) animateSlideContent(card);
						// If 2 slides visible, animate second
						if (slides[this.activeIndex + 1]) {
							const nextCard = slides[this.activeIndex + 1].querySelector('.hhm-testimonial-slider__card');
							if (nextCard) animateSlideContent(nextCard);
						}
					},
					slideChangeTransitionStart: function () {
						const activeSlide = this.slides[this.activeIndex];
						const card = activeSlide && activeSlide.querySelector('.hhm-testimonial-slider__card');
						if (card) animateSlideContent(card);
					},
				},
			});
		}

		// ----- 3. Card hover (GSAP) -----
		if (typeof gsap !== 'undefined') {
			section.querySelectorAll('.hhm-testimonial-slider__card').forEach(function (card) {
				card.addEventListener('mouseenter', function () {
					gsap.to(card, {
						y: -6,
						boxShadow: '0 20px 40px -12px rgba(0,0,0,0.12)',
						duration: 0.35,
						ease: easeSnappy,
					});
				});
				card.addEventListener('mouseleave', function () {
					gsap.to(card, {
						y: 0,
						boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
						duration: 0.4,
						ease: easeSnappy,
					});
				});
			});
		}
	}

	function init() {
		document.querySelectorAll('.hhm-testimonial-slider-section').forEach(initSection);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
